import type { OrderBy } from '@/lib/graphql-query-builder';
import { orgWallClock } from '@/lib/utils/custom-date/custom-date';
import { getOrganizationTimeZone } from '@/lib/utils/custom-date/org-time-zone';

export interface LunchMenu {
  ItemId: string;
  Name: string;
  WeekDay: string;
  MenuItems: string[];
  IsActive: boolean;
}

// Note: the .NET write model (Food.Domain/Models/FoodRequestResponseModel.cs) uses
// `UserName` rather than `DisplayName`, but the live Blocks schema for LunchRequest
// (SCHEMA_REFERENCE.json) deliberately keeps `DisplayName` for consistency
// with the rest of the data model (matching hr-manage-lunch, which owns the same
// entity admin-side), so those are the names used here.
export interface LunchRequest {
  ItemId: string;
  UserId: string;
  DisplayName?: string;
  MenuId?: string;
  MenuName?: string;
  MenuItems?: string[];
  /** The lunch day (date only): that day at 00:00:00 UTC, read by its date part. */
  RequestedForDate: string;
  CreatedDate?: string;
  DepartmentId?: string;
  DepartmentName?: string;
  DepartmentKey?: string;
}

export interface LunchRequestsPagingResult {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
  totalPages: number;
  pageSize: number;
  pageNo: number;
  items: LunchRequest[];
}

export interface GetLunchRequestsParams {
  pageNo: number;
  pageSize: number;
  filter?: Record<string, unknown>;
  /** Defaults to the latest lunch day first. */
  order?: OrderBy[];
}

/**
 * The lunch page's state for the lunch day: the caller's order already placed for it (they may cancel
 * it), else that day's active menus (they may order), else why neither (`MessageKey`). Read straight
 * from the Data Gateway in one query; LUNCH_CREATE and LUNCH_CANCEL re-check the menu, the day and the
 * order server-side.
 */
export interface LunchStatus {
  ShowLunchSubmitButton: boolean;
  ShowLunchCancelButton: boolean;
  /** Translation key (lunch-system) of why the user can neither order nor cancel; '' otherwise. */
  MessageKey: string;
  MessageParams?: Record<string, string>;
  FoodRequestId: string;
  /** MenuItems snapshot of the order already placed. */
  RequestedMenu: string[] | null;
  /** Menu name of the order already placed. */
  MenuName: string;
  /** The day's active menus. */
  MenuList: LunchMenu[];
}

/** The caller's order for a lunch day, as the status read answers it. */
export type LunchOrder = Pick<LunchRequest, 'ItemId' | 'MenuName' | 'MenuItems'>;

/** Days lunch is never served (the default organization's weekend, as the legacy portal had it). */
const LUNCH_WEEKEND_DAYS = ['Friday', 'Saturday'];

/** The page state for lunch day `day` ("YYYY-MM-DD", weekday `weekDay`). */
export const lunchStatusOf = (
  day: string,
  weekDay: string,
  order: LunchOrder | undefined,
  menus: LunchMenu[],
  isWorkDay?: boolean
): LunchStatus => {
  const status: LunchStatus = {
    ShowLunchSubmitButton: false,
    ShowLunchCancelButton: false,
    MessageKey: '',
    FoodRequestId: '',
    RequestedMenu: null,
    MenuName: '',
    MenuList: [],
  };
  if (order) {
    return {
      ...status,
      ShowLunchCancelButton: true,
      FoodRequestId: order.ItemId,
      MenuName: order.MenuName ?? '',
      RequestedMenu: order.MenuItems ?? null,
    };
  }
  const isWeekend = isWorkDay !== undefined ? !isWorkDay : LUNCH_WEEKEND_DAYS.includes(weekDay);
  if (isWeekend)
    return { ...status, MessageKey: 'LUNCH_NOT_ON_WEEKEND' };
  // Ordering opens only on a work day that has an active menu.
  if (menus.length > 0) return { ...status, ShowLunchSubmitButton: true, MenuList: menus };
  return { ...status, MessageKey: 'LUNCH_NO_ACTIVE_MENU', MessageParams: { day } };
};

/**
 * Response of the LUNCH_CREATE workflow (blocks/workflows/lunch-create-*.json). The workflow takes
 * only the menu id; requester, department and target date are derived server-side.
 */
export interface LunchCreateResult {
  IsSuccessful: boolean;
  /** English fallback; the app shows `Data.MessageKey` translated. */
  Message: string;
  EntityId: string;
  Data: { MessageKey?: string; MessageParams?: Record<string, string | number> } | null;
}

/** Lunch for a day can be ordered until 11:00 that day on the organization's clock. */
export const LUNCH_CUTOFF_SECONDS = 11 * 60 * 60;

/**
 * The lunch day ("YYYY-MM-DD") an order placed at `now` is for — the same rule the LUNCH_CREATE
 * workflow applies: up to 11:00 in the organization's time zone it is that day, after
 * 11:00 the next one. The day is the employee's local day, never the device's or UTC's.
 */
export const getTargetLunchDay = (
  now: Date = new Date(),
  timeZone: string = getOrganizationTimeZone()
): string => {
  const { year, month, day, hour, minute, second } = orgWallClock(now, timeZone);
  const afterCutoff = hour * 3600 + minute * 60 + second > LUNCH_CUTOFF_SECONDS;
  return new Date(Date.UTC(year, month - 1, day + (afterCutoff ? 1 : 0)))
    .toISOString()
    .slice(0, 10);
};
